import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { Save } from 'lucide-react';

const ZoneForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [provinces, setProvinces] = useState<any[]>([]);

  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      province_id: ''
    }
  });

  useEffect(() => {
    fetchProvinces();
    
    if (isEditing) {
      fetchZone();
    }
  }, [isEditing, id]);

  const fetchZone = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const { data: zone, error } = await supabase
        .from('zones')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      if (zone) {
        // Set form values
        setValue('name', zone.name);
        setValue('province_id', zone.province_id);
      }
    } catch (error) {
      console.error('Error fetching zone:', error);
      toast.error('Errore nel caricamento della zona');
    } finally {
      setLoading(false);
    }
  };

  const fetchProvinces = async () => {
    try {
      const { data, error } = await supabase
        .from('provinces')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      
      setProvinces(data || []);
    } catch (error) {
      console.error('Error fetching provinces:', error);
      toast.error('Errore nel caricamento delle province');
    }
  };

  const onSubmit = async (formData: any) => {
    setSubmitLoading(true);
    
    try {
      if (isEditing) {
        const { error } = await supabase
          .from('zones')
          .update(formData)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Zona aggiornata con successo');
      } else {
        const { error } = await supabase
          .from('zones')
          .insert(formData);
        
        if (error) throw error;
        
        toast.success('Zona creata con successo');
      }
      
      navigate('/admin/zones');
    } catch (error: any) {
      console.error('Error saving zone:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante il salvataggio'}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? "Modifica Zona" : "Nuova Zona"}>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? "Modifica Zona" : "Nuova Zona"}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Informazioni Zona</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome*
              </label>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Il nome è obbligatorio' }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. Centro"
                  />
                )}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Città/Comune*
              </label>
              <Controller
                name="province_id"
                control={control}
                rules={{ required: 'La provincia è obbligatoria' }}
                render={({ field }) => (
                  <select
                    {...field}
                    className={`w-full border ${errors.province_id ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                  >
                    <option value="">Seleziona città/comune</option>
                    {provinces.map(province => (
                      <option key={province.id} value={province.id}>
                        {province.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.province_id && (
                <p className="text-red-500 text-sm mt-1">{errors.province_id.message}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/zones')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitLoading}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitLoading ? (
              <>
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Salva Zona
              </>
            )}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default ZoneForm;